import React, { useMemo } from "react"

import { Clock, Edit, List, Map as MapIcon, MapPin, Plus } from "lucide-react"

import { TableEmptyRow } from "@/components/shared/EmptyState"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { TabsContent } from "@/components/ui/tabs"
import DirectoryCoverageFilters from "@/components/collections/planning/DirectoryCoverageFilters"
import DirectoryMapView from "@/components/collections/planning/DirectoryMapView"
import {
  CoverageFilter,
  coverageFor,
  describeCoverageFilter,
  isCoverageFilterActive,
} from "@/lib/collectionCoverage"
import { cn } from "@/lib/utils"
import { Customer, Location, Slot } from "@/types/collections"

import { getStatusColor } from "./badgeColors"
import { LocationsView } from "./types"

interface LocationsTabProps {
  /** Already narrowed by the directory search. */
  locations: Location[]
  slots: Slot[]
  customersById: Map<string, Customer>
  coverageFilter: CoverageFilter
  onCoverageFilterChange: (filter: CoverageFilter) => void
  view: LocationsView
  onViewChange: (view: LocationsView) => void
  onNew: () => void
  onEdit: (location: Location) => void
  onOpenSite: (location: Location) => void
  onRevealCustomer: (customerId: string) => void
}

const LocationsTab: React.FC<LocationsTabProps> = ({
  locations,
  slots,
  customersById,
  coverageFilter,
  onCoverageFilterChange,
  view,
  onViewChange,
  onNew,
  onEdit,
  onOpenSite,
  onRevealCustomer,
}) => {
  /** Coverage filter applied on top of the search filter, so both constrain. */
  const coveredLocations = useMemo(
    () => coverageFor(locations, slots, coverageFilter),
    [locations, slots, coverageFilter],
  )

  const coverageActive = isCoverageFilterActive(coverageFilter)
  const coverageDescription = describeCoverageFilter(coverageFilter)

  const slotsAt = (locationId: string): Slot[] => slots.filter((s) => s.location_id === locationId)

  return (
    <TabsContent value="locations" className="m-0 h-full">
      <div className="bg-white rounded-lg border border-palette-jungleGreen200 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-palette-jungleGreen200">
          <h3 className="font-medium text-palette-jungleGreen900">All Locations</h3>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-0.5 rounded-md border border-palette-jungleGreen200 p-0.5">
              {(
                [
                  { value: "list", label: "List", icon: List },
                  { value: "map", label: "Map", icon: MapIcon },
                ] as { value: LocationsView; label: string; icon: typeof List }[]
              ).map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => onViewChange(value)}
                  aria-pressed={view === value}
                  className={cn(
                    "flex h-6 items-center gap-1 rounded px-2 text-xs font-medium transition-colors",
                    view === value
                      ? "bg-palette-jungleGreen100 text-palette-jungleGreen900"
                      : "text-palette-jungleGreen500 hover:text-palette-jungleGreen900",
                  )}
                >
                  <Icon size={12} />
                  {label}
                </button>
              ))}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onNew}
              className="h-7 bg-white border-palette-jungleGreen200 text-palette-jungleGreen600 hover:bg-palette-jungleGreen000 gap-1.5 text-xs"
            >
              <Plus size={12} />
              New Location
            </Button>
          </div>
        </div>

        <DirectoryCoverageFilters
          filter={coverageFilter}
          onChange={onCoverageFilterChange}
          resultCount={coveredLocations.length}
          totalCount={locations.length}
        />

        {view === "map" ? (
          /* --app-vh, not vh: the app renders at 75% zoom (see index.css). */
          <div className="h-[calc(var(--app-vh)-19rem)] min-h-[360px] w-full">
            <DirectoryMapView
              covered={coveredLocations}
              customersById={customersById}
              onSelectLocation={(id) => {
                const location = locations.find((l) => l.id === id)
                if (location) onEdit(location)
              }}
              filterDescription={coverageDescription}
            />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-palette-jungleGreen000/50">
                <TableHead className="text-xs">Site Name</TableHead>
                <TableHead className="text-xs">Location ID</TableHead>
                <TableHead className="text-xs">Internal ID</TableHead>
                <TableHead className="text-xs">Customer</TableHead>
                <TableHead className="text-xs">Address</TableHead>
                <TableHead className="text-xs">Hours</TableHead>
                <TableHead className="text-xs text-center">Loading</TableHead>
                <TableHead className="text-xs text-center">Slots</TableHead>
                <TableHead className="text-xs">Status</TableHead>
                <TableHead className="text-xs text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {coveredLocations.length === 0 ? (
                <TableEmptyRow colSpan={10}>
                  {coverageActive
                    ? `Nothing to collect (${coverageDescription})`
                    : "No locations found"}
                </TableEmptyRow>
              ) : (
                coveredLocations.map(({ location, slots: matchingSlots }) => {
                  const locationCustomers =
                    location.customers
                      ?.map((lc) => customersById.get(lc.customer_id))
                      .filter(Boolean) || []
                  const legacyCustomer = location.customer_id
                    ? customersById.get(location.customer_id)
                    : null
                  const displayCustomers =
                    locationCustomers.length > 0
                      ? locationCustomers
                      : legacyCustomer
                        ? [legacyCustomer]
                        : []
                  const locationSlots = slotsAt(location.id)
                  return (
                    <TableRow key={location.id} className="hover:bg-palette-jungleGreen000">
                      <TableCell className="font-medium text-sm">
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-palette-jungleGreen400" />
                          {location.site_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-palette-jungleGreen600 font-mono">
                        {location.location_id}
                      </TableCell>
                      <TableCell className="text-sm text-palette-jungleGreen600 font-mono">
                        {location.internal_id || (
                          <span className="text-palette-jungleGreen400 italic">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {displayCustomers.length > 0 ? (
                          <div className="flex flex-wrap items-center gap-1">
                            {displayCustomers.slice(0, 2).map((customer) => (
                              <button
                                type="button"
                                key={customer!.id}
                                onClick={() => onRevealCustomer(customer!.id)}
                                title={`Show ${customer!.name} and its sites`}
                                className="text-blue-600 hover:underline cursor-pointer flex items-center gap-0.5"
                              >
                                {customer!.name}
                              </button>
                            ))}
                            {displayCustomers.length > 2 && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                +{displayCustomers.length - 2}
                              </Badge>
                            )}
                          </div>
                        ) : (
                          <span className="text-palette-jungleGreen400 italic">
                            No customer
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-palette-jungleGreen600">
                        <div>{location.address}</div>
                        <div className="text-xs text-palette-jungleGreen400">
                          {location.postcode}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-palette-jungleGreen600">
                        <div className="flex items-center gap-1">
                          <Clock size={12} className="text-palette-jungleGreen400" />
                          {location.opening_time} - {location.closing_time}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-center">
                        {location.parcels_per_loading_unit ? (
                          <div className="text-palette-jungleGreen900">
                            {location.parcels_per_loading_unit}
                          </div>
                        ) : (
                          <div className="text-palette-jungleGreen400">-</div>
                        )}
                        <div className="text-[10px] text-palette-jungleGreen400">
                          {location.loading_unit_type || "N/A"}
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {/* While filtered, counts only the slots that match the filter. */}
                        <Badge
                          variant="outline"
                          className="bg-palette-jungleGreen000 gap-1 pointer-events-none"
                          title={
                            coverageActive
                              ? `${matchingSlots.length} of ${locationSlots.length} slots match`
                              : undefined
                          }
                        >
                          <Clock size={10} />
                          {coverageActive ? matchingSlots.length : locationSlots.length}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            "px-2 py-0.5 text-xs rounded border",
                            getStatusColor(location.status),
                          )}
                        >
                          {location.status === "pending"
                            ? "Pending"
                            : location.status.charAt(0).toUpperCase() +
                              location.status.slice(1)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => onOpenSite(location)}
                        >
                          Collections
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Edit site details and customers"
                          onClick={() => onEdit(location)}
                        >
                          <Edit size={14} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        )}
      </div>
    </TabsContent>
  )
}

export default LocationsTab
